import { useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';

import { CELL_WIDTH_REM } from '@/modules/planner/constants';
import usePlannerStore from '@/modules/planner/store';

interface IProps {
  id: number;
  originalStartDate: dayjs.Dayjs;
  originalEndDate: dayjs.Dayjs;
  onCut: (cutDate: dayjs.Dayjs) => void;
}
export const useCutMode = ({ id, originalStartDate, originalEndDate, onCut }: IProps) => {
  const zoomLevel = usePlannerStore(store => store.zoomLevel);
  const activeCutId = usePlannerStore(store => store.activeCutId);
  const setActiveCutId = usePlannerStore(store => store.setActiveCutId);
  const dates = usePlannerStore(store => store.dates);
  const setInteractingAssignmentId = usePlannerStore(store => store.setInteractingAssignmentId);
  const setActiveCutFragmentStart = usePlannerStore(store => store.setActiveCutFragmentStart);
  const assignmentRef = useRef<HTMLDivElement>(null);

  const isCutting = activeCutId === id;

  const [cutOffset, setCutOffset] = useState<number | null>(null);

  const cellSize = CELL_WIDTH_REM * zoomLevel;

  // Use original (full) assignment dates for duration and positioning,
  // so the cut line spans the entire assignment, not just individual fragments
  const durationDays =
    originalEndDate.startOf('day').diff(originalStartDate.startOf('day'), 'day') + 1;
  const originalDuration = durationDays * cellSize;
  // A single-day assignment can't be cut regardless of zoom/minimize, so compare day
  // count (not rem width, which only equaled the base cell width at zoom level 1).
  const disableCutting = durationDays <= 1;

  // Position relative to planner grid (same approach as useDragResizeAssignment)
  const baseDate = dates[0];
  const cutStartOffset =
    originalStartDate.startOf('day').diff(baseDate.startOf('day'), 'day') * cellSize;

  const handleMouseMoveForCut = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isCutting) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const xPx = e.clientX - rect.left - 16;

    // convert px -> rem
    const remSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const xRem = xPx > 0 ? xPx / remSize : 0;

    const durationDays = Math.max(1, Math.round(originalDuration / cellSize));
    // Clamp to valid range: cannot cut on first or last day
    let dayIndex = Math.floor(xRem / cellSize + 1);
    const maxIndex = durationDays - 1;

    dayIndex = Math.min(Math.max(0, dayIndex), maxIndex);

    setCutOffset(dayIndex * cellSize);
  };

  const handleClickForCut = () => {
    if (isCutting && cutOffset !== null) {
      // Cut date is directly relative to originalStartDate since we now operate on the full assignment
      const cutOffsetDays = cutOffset / cellSize;
      const cutDate = originalStartDate.add(cutOffsetDays, 'day');
      onCut(cutDate);
      setActiveCutId(null);
      setCutOffset(null);
      setActiveCutFragmentStart(null);
      setInteractingAssignmentId(null);
    }
  };

  const toggleCutting = (fragmentStart?: dayjs.Dayjs, fragmentEnd?: dayjs.Dayjs) => {
    if (!isCutting) {
      setActiveCutId(id);
      setInteractingAssignmentId(id);
      // Store which fragment the user interacted with so it becomes the active cutter
      if (fragmentStart) {
        setActiveCutFragmentStart(fragmentStart.startOf('day').toISOString());
      }

      // Place initial cut line at the middle of the triggering fragment
      let initialOffset = Math.floor(originalDuration / (2 * cellSize)) * cellSize;
      if (fragmentStart && fragmentEnd) {
        const fragMidDays = Math.floor(
          fragmentEnd.startOf('day').diff(fragmentStart.startOf('day'), 'day') / 2
        );
        const fragMidDate = fragmentStart.add(fragMidDays, 'day');
        const daysFromOrigStart = fragMidDate
          .startOf('day')
          .diff(originalStartDate.startOf('day'), 'day');
        initialOffset = daysFromOrigStart * cellSize;
      }

      setCutOffset(initialOffset);
    } else {
      setActiveCutId(null);
      setInteractingAssignmentId(null);
      setActiveCutFragmentStart(null);
      setCutOffset(null);
    }
  };

  // Turn off cutting mode on Escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isCutting) {
        setActiveCutId(null);
        setInteractingAssignmentId(null);
        setActiveCutFragmentStart(null);
        setCutOffset(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCutting, setActiveCutId, setInteractingAssignmentId, setActiveCutFragmentStart]);

  // Turn off cutting mode when clicking outside assignment
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!isCutting) return;

      const target = e.target as HTMLElement;

      if (target.closest('[data-cut-toggle]')) return;

      // Check if clicked on the assignment element itself
      const clickedAssignment = target.closest('[data-assignment-id]');
      const clickedAssignmentId = clickedAssignment?.getAttribute('data-assignment-id');

      // Only close if clicked outside the assignment
      if (!clickedAssignmentId || Number(clickedAssignmentId) !== id) {
        setActiveCutId(null);
        setInteractingAssignmentId(null);
        setActiveCutFragmentStart(null);
        setCutOffset(null);
      }
    };

    if (isCutting) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCutting, setActiveCutId, setInteractingAssignmentId, setActiveCutFragmentStart, id]);

  return {
    isCutting,
    cutOffset,
    cutStartOffset,
    cutDuration: originalDuration,
    disableCutting,
    handleMouseMoveForCut,
    handleClickForCut,
    toggleCutting,
    assignmentRef,
  };
};
